import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'react-toastify';
import {
  FiX, FiSave, FiUpload, FiTrash2, FiPlus, FiPrinter, FiEye, FiEyeOff,
  FiRotateCcw, FiExternalLink,
} from 'react-icons/fi';
import { pdfLayoutAPI } from '../../services/api';
import {
  generateTruckReceiptNCCPDF,
  generateTruckReceiptNCCCalibrationPDF,
  getTruckReceiptNCCFields,
  TRUCK_RECEIPT_NCC_DOC_TYPE,
} from './TruckReceiptNCCPDF';
import {
  generateTruckReceiptPDF,
  getTruckReceiptNNCFields,
  TRUCK_RECEIPT_NNC_DOC_TYPE,
} from './OtherDocumentsPDF';

// ══════════════════════════════════════════════════════════════════════
// TRUCK RECEIPT — VISUAL LAYOUT EDITOR (NNC and NCC pre-printed forms)
//
// Full-screen editor for aligning a truck-receipt overlay to its
// pre-printed Jain Parivahan stationery. Every printable field is a
// draggable box on a 297 × 210 mm sheet; an uploaded scan of the blank
// form can sit underneath for visual alignment. Offsets (whole-page +
// per-field) and per-field "don't print" flags are saved as named layout
// profiles in the backend (pdf_layout_profiles); the active profile is
// applied automatically whenever that PDF is generated.
//
// A scan is a visual aid only — scanners skew and scale a few mm, so the
// final check is always a test print or the calibration sheet.
// ══════════════════════════════════════════════════════════════════════

const PAGE_W = 297; // mm, A4 landscape
const PAGE_H = 210;

// Pre-printed rules of the NCC form (mm), from the measured geometry in
// TruckReceiptNCCPDF.js — drawn as faint guides so the editor is usable
// before any scan is uploaded. The NNC form's rules were never measured
// (only its blanks), so that variant has no guides — upload a scan or use
// a test print to align it.
const NCC_GUIDES = {
  h: [
    [210, 58.0, 293], [210, 64.0, 293], [210, 70.3, 293], [210, 79.4, 293],
    [210, 90.5, 293], [210, 98.0, 293], [210, 99.4, 293],
    [131.6, 88.0, 208], [131.6, 93.7, 208], [131.6, 98.5, 208],
    [95, 115.4, 208], [46, 120.5, 208],
    [100.5, 130.3, 208], [46, 135.6, 208],
    [41, 138.2, 293], [41, 147.5, 293], [41, 188.5, 293],
    [51, 197.4, 113],
  ],
  v: [
    [41, 138.2, 188.5], [61, 138.2, 188.5], [157.5, 138.2, 188.5],
    [179.5, 138.2, 188.5], [201.5, 138.2, 188.5], [223.5, 138.2, 188.5],
    [293, 138.2, 188.5],
  ],
  rects: [
    { x: 210, y: 104.3, w: 83, h: 8.7 },  // From box
    { x: 210, y: 118.7, w: 83, h: 8.6 },  // To box
  ],
};

// Everything form-specific, keyed by the layout profile doc type.
const FORM_CONFIGS = {
  [TRUCK_RECEIPT_NCC_DOC_TYPE]: {
    title: 'Truck Receipt (NCC)',
    defaultName: 'Jain Parivahan NCC',
    getFields: getTruckReceiptNCCFields,
    guides: NCC_GUIDES,
    generate: generateTruckReceiptNCCPDF,
    calibration: generateTruckReceiptNCCCalibrationPDF,
  },
  [TRUCK_RECEIPT_NNC_DOC_TYPE]: {
    title: 'Truck Receipt (NNC)',
    defaultName: 'Jain Parivahan NNC',
    getFields: getTruckReceiptNNCFields,
    guides: null,
    generate: generateTruckReceiptPDF,
    calibration: null,
  },
};

const round1 = (v) => Math.round(v * 10) / 10;

const emptyLayout = () => ({ ox: 0, oy: 0, fields: {} });

const parseProfileData = (profile) => {
  try {
    const data = JSON.parse(profile.data || '{}');
    return {
      ox: Number(data.ox) || 0,
      oy: Number(data.oy) || 0,
      fields: data.fields && typeof data.fields === 'object' ? data.fields : {},
    };
  } catch {
    return emptyLayout();
  }
};

const btnStyle = (variant) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: 6,
  padding: '8px 14px',
  borderRadius: 10,
  fontSize: 13,
  fontWeight: 600,
  cursor: 'pointer',
  fontFamily: 'Inter, sans-serif',
  whiteSpace: 'nowrap',
  ...(variant === 'primary'
    ? { background: '#059669', border: '1px solid #059669', color: '#ffffff' }
    : variant === 'danger'
      ? { background: '#ffffff', border: '1px solid #fca5a5', color: '#dc2626' }
      : { background: '#ffffff', border: '1px solid rgba(16,185,129,0.35)', color: '#047857' }),
});

const numInputStyle = {
  width: 64,
  padding: '6px 8px',
  borderRadius: 8,
  border: '1px solid rgba(16,185,129,0.35)',
  fontSize: 13,
  fontFamily: 'Consolas, monospace',
  color: '#064e3b',
};

function TruckReceiptLayoutEditor({ docType = TRUCK_RECEIPT_NCC_DOC_TYPE, doc, company, authority, onClose, onApply }) {
  const config = FORM_CONFIGS[docType] || FORM_CONFIGS[TRUCK_RECEIPT_NCC_DOC_TYPE];
  const fields = useMemo(() => config.getFields(), [config]);

  const [profiles, setProfiles] = useState([]);
  const [profileId, setProfileId] = useState(null); // null = unsaved new profile
  const [name, setName] = useState(config.defaultName);
  const [layout, setLayout] = useState(emptyLayout);
  const [selectedKey, setSelectedKey] = useState(null);
  const [scanUrl, setScanUrl] = useState(null);
  const [scanOpacity, setScanOpacity] = useState(0.6);
  const [zoom, setZoom] = useState(1);
  const [snap, setSnap] = useState(true);
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  const canvasRef = useRef(null);
  const dragRef = useRef(null); // { key, startX, startY, startDx, startDy, pxPerMm }
  const scanUrlRef = useRef(null);

  // ── Load profiles ─────────────────────────────────────────────────────
  const loadProfiles = useCallback(async (preferId) => {
    try {
      const res = await pdfLayoutAPI.getAll(docType);
      const list = res.data || [];
      setProfiles(list);
      const pick = list.find((p) => p.id === preferId) || list.find((p) => p.is_active) || list[0];
      if (pick) {
        setProfileId(pick.id);
        setName(pick.name);
        setLayout(parseProfileData(pick));
        setDirty(false);
      }
    } catch (err) {
      console.error('Failed to load layout profiles:', err);
      toast.error('Failed to load layout profiles');
    }
  }, [docType]);

  useEffect(() => {
    loadProfiles();
  }, [loadProfiles]);

  // Revoke the scan object URL on unmount
  useEffect(() => () => {
    if (scanUrlRef.current) URL.revokeObjectURL(scanUrlRef.current);
  }, []);

  const grid = round1(snap ? 0.5 : 0.1);

  const fieldAdj = useCallback(
    (key) => layout.fields[key] || { dx: 0, dy: 0, hide: false },
    [layout]
  );

  const setFieldAdj = useCallback((key, patch) => {
    setLayout((prev) => {
      const cur = prev.fields[key] || { dx: 0, dy: 0, hide: false };
      return { ...prev, fields: { ...prev.fields, [key]: { ...cur, ...patch } } };
    });
    setDirty(true);
  }, []);

  // ── Dragging ──────────────────────────────────────────────────────────
  const onBoxPointerDown = (e, key) => {
    e.preventDefault();
    e.stopPropagation();
    setSelectedKey(key);
    const canvas = canvasRef.current;
    if (!canvas) return;
    const pxPerMm = canvas.clientWidth / PAGE_W;
    const adj = fieldAdj(key);
    dragRef.current = {
      key,
      startX: e.clientX,
      startY: e.clientY,
      startDx: adj.dx || 0,
      startDy: adj.dy || 0,
      pxPerMm,
    };
    e.currentTarget.setPointerCapture(e.pointerId);
  };

  const onBoxPointerMove = (e) => {
    const d = dragRef.current;
    if (!d) return;
    const dxMm = (e.clientX - d.startX) / d.pxPerMm;
    const dyMm = (e.clientY - d.startY) / d.pxPerMm;
    const step = snap ? 0.5 : 0.1;
    const snapTo = (v) => round1(Math.round(v / step) * step);
    setFieldAdj(d.key, { dx: snapTo(d.startDx + dxMm), dy: snapTo(d.startDy + dyMm) });
  };

  const onBoxPointerUp = () => {
    dragRef.current = null;
  };

  // ── Keyboard nudge (arrow keys; Shift = 1 mm steps) ───────────────────
  const onKeyDown = (e) => {
    if (!selectedKey) return;
    const step = e.shiftKey ? 1 : 0.1;
    const adj = fieldAdj(selectedKey);
    let { dx = 0, dy = 0 } = adj;
    if (e.key === 'ArrowLeft') dx -= step;
    else if (e.key === 'ArrowRight') dx += step;
    else if (e.key === 'ArrowUp') dy -= step;
    else if (e.key === 'ArrowDown') dy += step;
    else return;
    e.preventDefault();
    setFieldAdj(selectedKey, { dx: round1(dx), dy: round1(dy) });
  };

  // ── Scan upload ───────────────────────────────────────────────────────
  const onScanUpload = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (scanUrlRef.current) URL.revokeObjectURL(scanUrlRef.current);
    const url = URL.createObjectURL(file);
    scanUrlRef.current = url;
    setScanUrl(url);
    e.target.value = '';
  };

  // ── Profile actions ───────────────────────────────────────────────────
  const switchProfile = (id) => {
    if (dirty && !window.confirm('Discard unsaved layout changes?')) return;
    if (id === 'new') {
      setProfileId(null);
      setName('New layout profile');
      setLayout(emptyLayout());
      setDirty(false);
      return;
    }
    const p = profiles.find((x) => x.id === Number(id));
    if (p) {
      setProfileId(p.id);
      setName(p.name);
      setLayout(parseProfileData(p));
      setDirty(false);
    }
  };

  const saveProfile = async () => {
    const trimmed = name.trim();
    if (!trimmed) {
      toast.error('Give the layout profile a name');
      return;
    }
    setSaving(true);
    try {
      let saved;
      if (profileId) {
        const res = await pdfLayoutAPI.update(profileId, { name: trimmed, data: layout, is_active: true });
        saved = res.data;
      } else {
        const res = await pdfLayoutAPI.create({
          doc_type: docType,
          name: trimmed,
          data: layout,
          is_active: true,
        });
        saved = res.data;
      }
      setDirty(false);
      await loadProfiles(saved.id);
      toast.success(`Layout profile saved — it will be used for all ${config.title} prints`);
      if (onApply) onApply(layout);
    } catch (err) {
      console.error('Failed to save layout profile:', err);
      toast.error(err.response?.data?.error || 'Failed to save layout profile');
    } finally {
      setSaving(false);
    }
  };

  const deleteProfile = async () => {
    if (!profileId) return;
    if (!window.confirm(`Delete layout profile "${name}"? Prints revert to the built-in layout.`)) return;
    try {
      await pdfLayoutAPI.delete(profileId);
      toast.success('Layout profile deleted');
      setProfileId(null);
      setLayout(emptyLayout());
      setDirty(false);
      await loadProfiles();
      if (onApply) onApply(null);
    } catch (err) {
      console.error('Failed to delete layout profile:', err);
      toast.error(err.response?.data?.error || 'Failed to delete layout profile');
    }
  };

  const resetOffsets = () => {
    if (!window.confirm('Reset every offset in this profile to 0 and show all fields?')) return;
    setLayout(emptyLayout());
    setDirty(true);
  };

  // ── Test outputs (open in a new tab, current working layout) ──────────
  const openBlob = (blob) => {
    const url = URL.createObjectURL(blob);
    window.open(url, '_blank');
    setTimeout(() => URL.revokeObjectURL(url), 60000);
  };

  const previewPdf = () => {
    try {
      openBlob(config.generate(doc, company, authority, true, layout));
    } catch (err) {
      console.error('Preview failed:', err);
      toast.error('Failed to generate preview');
    }
  };

  const previewCalibration = () => {
    if (!config.calibration) return;
    try {
      openBlob(config.calibration(doc, company, authority, true, layout));
    } catch (err) {
      console.error('Calibration preview failed:', err);
      toast.error('Failed to generate calibration sheet');
    }
  };

  // ── Derived render values ─────────────────────────────────────────────
  const sheetWidthPx = Math.round(880 * zoom);
  const pxPerMm = sheetWidthPx / PAGE_W;
  const mm = (v) => v * pxPerMm;

  const adjustedCount = Object.values(layout.fields)
    .filter((f) => f && ((f.dx || 0) !== 0 || (f.dy || 0) !== 0 || f.hide)).length;

  const selected = selectedKey ? fieldAdj(selectedKey) : null;
  const selectedField = selectedKey ? fields.find((f) => f.key === selectedKey) : null;

  return (
    <div
      style={{
        position: 'fixed', inset: 0, zIndex: 100000,
        background: 'rgba(0, 0, 0, 0.8)', backdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 14,
      }}
      onClick={() => { if (!dirty || window.confirm('Discard unsaved layout changes?')) onClose(); }}
    >
      <div
        style={{
          background: '#f8fefb', borderRadius: 16, width: '98vw', height: '95vh',
          display: 'flex', flexDirection: 'column', overflow: 'hidden',
          boxShadow: '0 24px 48px -12px rgba(0,0,0,0.3)',
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {/* Header */}
        <div
          style={{
            background: 'linear-gradient(135deg, #047857 0%, #059669 100%)',
            padding: '14px 22px', display: 'flex', alignItems: 'center', gap: 14,
          }}
        >
          <div style={{ flex: 1, minWidth: 0 }}>
            <h3 style={{ margin: 0, color: '#ffffff', fontSize: 17, fontWeight: 700, fontFamily: 'Poppins, sans-serif' }}>
              Print Layout — {config.title}
            </h3>
            <p style={{ margin: 0, fontSize: 12, color: 'rgba(255,255,255,0.85)' }}>
              Drag fields onto the form's blanks · arrow keys nudge 0.1 mm (Shift = 1 mm) ·{' '}
              {adjustedCount} field{adjustedCount === 1 ? '' : 's'} adjusted{dirty ? ' · unsaved changes' : ''}
            </p>
          </div>

          {/* Profile picker */}
          <select
            value={profileId || 'new'}
            onChange={(e) => switchProfile(e.target.value)}
            style={{
              padding: '8px 10px', borderRadius: 10, border: 'none', fontSize: 13,
              fontFamily: 'Inter, sans-serif', color: '#064e3b', maxWidth: 240,
            }}
            title="Layout profile"
          >
            {profiles.map((p) => (
              <option key={p.id} value={p.id}>
                {p.name}{p.is_active ? ' ● active' : ''}
              </option>
            ))}
            <option value="new">＋ New profile…</option>
          </select>

          <button style={btnStyle('primary')} onClick={saveProfile} disabled={saving}>
            <FiSave size={15} /> {saving ? 'Saving…' : 'Save & use'}
          </button>
          <button
            style={{ ...btnStyle(), background: 'rgba(255,255,255,0.15)', border: '1px solid rgba(255,255,255,0.35)', color: '#ffffff' }}
            onClick={() => { if (!dirty || window.confirm('Discard unsaved layout changes?')) onClose(); }}
          >
            <FiX size={16} /> Close
          </button>
        </div>

        {/* Body */}
        <div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
          {/* Canvas zone */}
          <div style={{ flex: 1, display: 'flex', flexDirection: 'column', minWidth: 0, background: '#eef7f1' }}>
            {/* Canvas toolbar */}
            <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '10px 16px', flexWrap: 'wrap', borderBottom: '1px solid rgba(16,185,129,0.15)' }}>
              <label style={{ ...btnStyle(), cursor: 'pointer' }}>
                <FiUpload size={14} /> Upload form scan
                <input type="file" accept="image/*" style={{ display: 'none' }} onChange={onScanUpload} />
              </label>
              {scanUrl && (
                <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#047857', fontWeight: 600 }}>
                  Scan opacity
                  <input
                    type="range" min="0.1" max="1" step="0.05" value={scanOpacity}
                    onChange={(e) => setScanOpacity(Number(e.target.value))}
                    style={{ width: 90 }}
                  />
                </label>
              )}
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#047857', fontWeight: 600 }}>
                Zoom
                <input
                  type="range" min="0.7" max="2.5" step="0.1" value={zoom}
                  onChange={(e) => setZoom(Number(e.target.value))}
                  style={{ width: 90 }}
                />
              </label>
              <label style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 12, color: '#047857', fontWeight: 600, cursor: 'pointer' }}>
                <input type="checkbox" checked={snap} onChange={(e) => setSnap(e.target.checked)} />
                Snap {grid} mm
              </label>
              <span style={{ flex: 1 }} />
              {config.calibration && (
                <button style={btnStyle()} onClick={previewCalibration} title="Calibration sheet with the current offsets — print it onto a blank form">
                  <FiPrinter size={14} /> Calibration sheet
                </button>
              )}
              <button style={btnStyle()} onClick={previewPdf} title="Open the Truck Receipt with the current layout in a new tab">
                <FiExternalLink size={14} /> Test preview
              </button>
            </div>

            {/* Scrollable sheet */}
            <div
              style={{ flex: 1, overflow: 'auto', padding: 18 }}
              tabIndex={0}
              onKeyDown={onKeyDown}
            >
              <div
                ref={canvasRef}
                style={{
                  position: 'relative',
                  width: sheetWidthPx,
                  height: Math.round(sheetWidthPx * (PAGE_H / PAGE_W)),
                  background: '#ffffff',
                  border: '1px solid #cbd5d1',
                  borderRadius: 3,
                  boxShadow: '0 4px 16px rgba(6,78,59,0.12)',
                  userSelect: 'none',
                  margin: '0 auto',
                }}
                onPointerDown={() => setSelectedKey(null)}
              >
                {/* Scan underlay */}
                {scanUrl && (
                  <img
                    src={scanUrl}
                    alt="Scanned NCC form"
                    style={{
                      position: 'absolute', inset: 0, width: '100%', height: '100%',
                      objectFit: 'fill', opacity: scanOpacity, pointerEvents: 'none',
                    }}
                  />
                )}

                {/* Pre-printed rule guides (hidden when a scan is shown) */}
                {!scanUrl && config.guides && (
                  <>
                    {config.guides.h.map(([x1, y, x2], i) => (
                      <div key={`h${i}`} style={{ position: 'absolute', left: mm(x1), top: mm(y), width: mm(x2 - x1), height: 1, background: '#c9c2b0', pointerEvents: 'none' }} />
                    ))}
                    {config.guides.v.map(([x, y1, y2], i) => (
                      <div key={`v${i}`} style={{ position: 'absolute', left: mm(x), top: mm(y1), width: 1, height: mm(y2 - y1), background: '#c9c2b0', pointerEvents: 'none' }} />
                    ))}
                    {config.guides.rects.map((r, i) => (
                      <div key={`r${i}`} style={{ position: 'absolute', left: mm(r.x), top: mm(r.y), width: mm(r.w), height: mm(r.h), border: '1px solid #c9c2b0', pointerEvents: 'none' }} />
                    ))}
                  </>
                )}
                {!scanUrl && !config.guides && (
                  <div style={{
                    position: 'absolute', top: 8, left: 8, right: 8,
                    fontSize: 12, color: '#9ca3af', pointerEvents: 'none',
                  }}>
                    No form guides for this variant — upload a scan of the blank form, or align with a test print.
                  </div>
                )}

                {/* Field boxes */}
                {fields.map((f) => {
                  const adj = fieldAdj(f.key);
                  const isSel = selectedKey === f.key;
                  const left = mm(f.box.x + (adj.dx || 0) + (layout.ox || 0));
                  const top = mm(f.box.y + (adj.dy || 0) + (layout.oy || 0));
                  return (
                    <div
                      key={f.key}
                      onPointerDown={(e) => onBoxPointerDown(e, f.key)}
                      onPointerMove={onBoxPointerMove}
                      onPointerUp={onBoxPointerUp}
                      title={`${f.id} · ${f.label}`}
                      style={{
                        position: 'absolute',
                        left, top,
                        width: mm(f.box.w),
                        height: mm(f.box.h),
                        boxSizing: 'border-box',
                        border: isSel ? '2px solid #059669' : `1px dashed ${adj.hide ? '#9ca3af' : '#10b981'}`,
                        background: isSel
                          ? 'rgba(16,185,129,0.25)'
                          : adj.hide ? 'rgba(156,163,175,0.15)' : 'rgba(16,185,129,0.10)',
                        borderRadius: 2,
                        cursor: 'move',
                        zIndex: isSel ? 3 : 2,
                        display: 'flex',
                        alignItems: 'flex-start',
                        overflow: 'hidden',
                        touchAction: 'none',
                      }}
                    >
                      <span
                        style={{
                          fontSize: Math.max(8, 2.2 * pxPerMm * 0.4),
                          fontFamily: 'Consolas, monospace',
                          fontWeight: 700,
                          color: adj.hide ? '#6b7280' : '#047857',
                          padding: '0 3px',
                          whiteSpace: 'nowrap',
                          textDecoration: adj.hide ? 'line-through' : 'none',
                        }}
                      >
                        {f.id}
                      </span>
                      {isSel && (
                        <span
                          style={{
                            position: 'absolute', top: -20, left: 0,
                            background: '#064e3b', color: '#d1fae5',
                            fontSize: 10, fontFamily: 'Consolas, monospace',
                            padding: '2px 6px', borderRadius: 5, whiteSpace: 'nowrap',
                          }}
                        >
                          Δ {round1(adj.dx || 0)}, {round1(adj.dy || 0)} mm{adj.hide ? ' · hidden' : ''}
                        </span>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Sidebar */}
          <div style={{ width: 300, borderLeft: '1px solid rgba(16,185,129,0.15)', background: '#ffffff', display: 'flex', flexDirection: 'column', minHeight: 0 }}>
            {/* Profile name + whole-page nudge */}
            <div style={{ padding: 14, borderBottom: '1px solid rgba(16,185,129,0.12)' }}>
              <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
                Profile name
              </label>
              <input
                value={name}
                onChange={(e) => { setName(e.target.value); setDirty(true); }}
                style={{
                  width: '100%', boxSizing: 'border-box', marginTop: 4, padding: '8px 10px',
                  borderRadius: 8, border: '1px solid rgba(16,185,129,0.35)', fontSize: 13,
                  color: '#064e3b', fontFamily: 'Inter, sans-serif',
                }}
                placeholder="e.g. Jain Parivahan NCC — 2026 batch"
              />
              <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginTop: 12, flexWrap: 'wrap' }}>
                <label style={{ fontSize: 11, fontWeight: 700, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.06em', width: '100%' }}>
                  Whole-page nudge (mm)
                </label>
                <label style={{ fontSize: 12, color: '#047857', display: 'flex', alignItems: 'center', gap: 5 }}>
                  X
                  <input
                    type="number" step="0.1" value={layout.ox}
                    onChange={(e) => { setLayout((p) => ({ ...p, ox: Number(e.target.value) || 0 })); setDirty(true); }}
                    style={numInputStyle}
                  />
                </label>
                <label style={{ fontSize: 12, color: '#047857', display: 'flex', alignItems: 'center', gap: 5 }}>
                  Y
                  <input
                    type="number" step="0.1" value={layout.oy}
                    onChange={(e) => { setLayout((p) => ({ ...p, oy: Number(e.target.value) || 0 })); setDirty(true); }}
                    style={numInputStyle}
                  />
                </label>
              </div>
              <p style={{ margin: '8px 0 0', fontSize: 11, color: '#6b7280' }}>
                Positive X moves right, positive Y moves down. Use this when every field is off by the same amount (printer feed drift).
              </p>
            </div>

            {/* Selected field detail */}
            {selectedField && selected && (
              <div style={{ padding: 14, borderBottom: '1px solid rgba(16,185,129,0.12)', background: '#ecfdf5' }}>
                <div style={{ fontSize: 12.5, fontWeight: 700, color: '#064e3b', marginBottom: 8 }}>
                  {selectedField.id} · {selectedField.label}
                </div>
                <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
                  <label style={{ fontSize: 12, color: '#047857', display: 'flex', alignItems: 'center', gap: 5 }}>
                    ΔX
                    <input
                      type="number" step="0.1" value={selected.dx || 0}
                      onChange={(e) => setFieldAdj(selectedKey, { dx: Number(e.target.value) || 0 })}
                      style={numInputStyle}
                    />
                  </label>
                  <label style={{ fontSize: 12, color: '#047857', display: 'flex', alignItems: 'center', gap: 5 }}>
                    ΔY
                    <input
                      type="number" step="0.1" value={selected.dy || 0}
                      onChange={(e) => setFieldAdj(selectedKey, { dy: Number(e.target.value) || 0 })}
                      style={numInputStyle}
                    />
                  </label>
                </div>
                <label style={{ display: 'flex', alignItems: 'center', gap: 7, marginTop: 10, fontSize: 12.5, color: '#064e3b', fontWeight: 600, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={!!selected.hide}
                    onChange={(e) => setFieldAdj(selectedKey, { hide: e.target.checked })}
                  />
                  Don't print this field
                </label>
              </div>
            )}

            {/* Field list */}
            <div style={{ flex: 1, overflowY: 'auto', padding: '8px 8px' }}>
              {fields.map((f) => {
                const adj = fieldAdj(f.key);
                const isSel = selectedKey === f.key;
                const changed = (adj.dx || 0) !== 0 || (adj.dy || 0) !== 0;
                return (
                  <div
                    key={f.key}
                    onClick={() => setSelectedKey(f.key)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 8,
                      padding: '6px 8px', borderRadius: 8, cursor: 'pointer',
                      background: isSel ? '#d1fae5' : 'transparent',
                      border: isSel ? '1px solid rgba(16,185,129,0.5)' : '1px solid transparent',
                    }}
                  >
                    <span style={{
                      fontFamily: 'Consolas, monospace', fontSize: 10.5, fontWeight: 700,
                      background: '#ecfdf5', color: '#047857', border: '1px solid rgba(16,185,129,0.3)',
                      borderRadius: 5, padding: '1px 5px', minWidth: 22, textAlign: 'center',
                    }}>
                      {f.id}
                    </span>
                    <span style={{
                      flex: 1, fontSize: 12, color: adj.hide ? '#9ca3af' : '#064e3b',
                      overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
                      textDecoration: adj.hide ? 'line-through' : 'none',
                    }}>
                      {f.label}
                    </span>
                    {changed && (
                      <span style={{ fontFamily: 'Consolas, monospace', fontSize: 10, color: '#047857', fontWeight: 700 }}>
                        {round1(adj.dx || 0)},{round1(adj.dy || 0)}
                      </span>
                    )}
                    <button
                      onClick={(e) => { e.stopPropagation(); setFieldAdj(f.key, { hide: !adj.hide }); }}
                      title={adj.hide ? 'Print this field' : "Don't print this field"}
                      style={{
                        border: 'none', background: 'transparent', cursor: 'pointer',
                        color: adj.hide ? '#9ca3af' : '#10b981', display: 'flex', padding: 2,
                      }}
                    >
                      {adj.hide ? <FiEyeOff size={14} /> : <FiEye size={14} />}
                    </button>
                  </div>
                );
              })}
            </div>

            {/* Footer actions */}
            <div style={{ padding: 12, borderTop: '1px solid rgba(16,185,129,0.12)', display: 'flex', gap: 8 }}>
              <button style={{ ...btnStyle(), flex: 1 }} onClick={resetOffsets}>
                <FiRotateCcw size={14} /> Reset all
              </button>
              {profileId ? (
                <button style={{ ...btnStyle('danger'), flex: 1 }} onClick={deleteProfile}>
                  <FiTrash2 size={14} /> Delete
                </button>
              ) : (
                <button style={{ ...btnStyle(), flex: 1 }} onClick={() => switchProfile('new')}>
                  <FiPlus size={14} /> New
                </button>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default TruckReceiptLayoutEditor;
